/**
 * ============================================================================
 * ReviewStats - Review Statistics KPI Cards
 * ============================================================================
 */

import React from 'react';
import { Star, MessageCircle, TrendingUp, AlertCircle } from 'lucide-react';
import type { ReviewStats as ReviewStatsType } from '../../types/reviews';

interface ReviewStatsProps {
  stats: ReviewStatsType;
}

export function ReviewStats({ stats }: ReviewStatsProps) {
  const statCards = [
    {
      label: 'Average Rating',
      value: stats.average_rating.toFixed(1),
      subtext: `${stats.total_reviews} total reviews`,
      icon: Star,
      iconClass: 'bg-yellow-50 text-yellow-600',
      showStar: true,
    },
    {
      label: 'Reply Rate',
      value: `${stats.reply_rate}%`,
      subtext: `${stats.total_replies} replies`,
      icon: MessageCircle,
      iconClass: 'bg-blue-50 text-blue-600',
      showStar: false,
    },
    {
      label: 'Recent Reviews',
      value: stats.recent_reviews.toString(),
      subtext: 'Last 7 days',
      icon: TrendingUp,
      iconClass: 'bg-green-50 text-green-600',
      showStar: false,
    },
    {
      label: 'Unanswered',
      value: stats.unanswered_reviews.toString(),
      subtext: 'Awaiting reply',
      icon: AlertCircle,
      iconClass: 'bg-orange-50 text-orange-600',
      showStar: false,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {statCards.map((stat, idx) => (
        <div key={idx} className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">{stat.label}</p>
              <div className="flex items-baseline gap-2">
                <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stat.value}</p>
                {stat.showStar && (
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                )}
              </div>
            </div>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${stat.iconClass} flex items-center justify-center flex-shrink-0 ml-2`}>
              <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
